import { Skeleton } from "@aurumiq/ui/components/skeleton";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { errorMessage } from "@/lib/api";

/** Renders loading skeletons / an error panel, otherwise `children(data)`. */
export function QueryState<T>({
  query,
  children,
  rows = 3,
}: {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
  rows?: number;
}) {
  if (query.isPending)
    return (
      <div className="grid gap-2" aria-busy="true">
        {Array.from({ length: rows }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows
          <Skeleton key={i} className="h-5 w-full" />
        ))}
      </div>
    );
  if (query.isError)
    return (
      <div
        role="alert"
        className="rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-danger text-sm"
      >
        Could not load: {errorMessage(query.error)}
      </div>
    );
  return <>{children(query.data as T)}</>;
}

/** Definition-list row helper for read-only cards. */
export function Stat({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="contents">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

export function StatList({ children }: { children: ReactNode }) {
  return (
    <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-1.5 text-sm">{children}</dl>
  );
}
